import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { AuthShell } from '@/components/AuthShell';
import { PasswordPolicy, passwordMeetsPolicy } from '@/components/PasswordPolicy';

export function ForceChangePassword() {
  const { state, forceChangePassword } = useAuth();
  const { t } = useTranslation('auth');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (state.status === 'authenticated') return <Navigate to="/" replace />;
  if (state.status === 'totp_setup') return <Navigate to="/totp-setup" replace />;
  if (state.status === 'totp_verify') return <Navigate to="/2fa" replace />;
  if (state.status !== 'password_expired') return <Navigate to="/login" replace />;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (newPassword !== confirm) {
      setError(t('forceChange.mismatch'));
      return;
    }
    setIsLoading(true);
    try {
      await forceChangePassword(newPassword);
      // State transition (totp_setup / totp_verify) redirects via the guards above.
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('common:error'));
      setIsLoading(false);
    }
  }

  const canSubmit = passwordMeetsPolicy(newPassword) && confirm.length > 0 && !isLoading;

  return (
    <AuthShell>
      <div>
        <h2 className="text-xl font-bold text-white">{t('forceChange.title')}</h2>
        <p className="text-white/60 mt-1 text-sm">{t('forceChange.subtitle')}</p>
      </div>

      {error && (
        <div className="px-4 py-2.5 rounded-lg bg-red-500/20 border border-red-400/30 text-red-200 text-sm font-medium">
          {error}
        </div>
      )}

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-white/80 mb-1.5">
            {t('forceChange.newPassword')}
          </label>
          <input
            type="password"
            id="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={isLoading}
            autoComplete="new-password"
            className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
          />
        </div>

        <PasswordPolicy password={newPassword} />

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-white/80 mb-1.5">
            {t('forceChange.confirmPassword')}
          </label>
          <input
            type="password"
            id="confirm-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={isLoading}
            autoComplete="new-password"
            className="glass-input w-full px-4 py-2.5 rounded-lg text-sm text-white focus:outline-none focus:border-brand-rose focus:bg-white/[0.22] focus:ring-[3px] focus:ring-brand-rose/25 disabled:opacity-50"
          />
        </div>

        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full py-2.5 rounded-lg text-white font-semibold text-sm shadow-lg bg-gradient-to-r from-brand-rose to-brand-orange hover:from-brand-purple hover:to-brand-rose transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
          {isLoading ? t('forceChange.submitting') : t('forceChange.submit')}
        </button>
      </form>
    </AuthShell>
  );
}
